import type { ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Menu as MenuIcon, PanelLeftOpen, SquarePen } from 'lucide-react'
import { cn } from '@/lib/cn'
import { ease } from '@/lib/motion'
import { MOBILE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useNewChat } from '@/hooks/useNewChat'
import { useUiStore } from '@/stores/uiStore'
import { IconButton } from '@/components/ui/IconButton'

type Props = {
  title: ReactNode
  children?: ReactNode
  /** Transparent bar laid over the page (welcome screen). */
  floating?: boolean
}

/** Frosted page header: sidebar controls (when collapsed / on mobile), title, page actions. */
export function TopBar({ title, children, floating = false }: Props) {
  const isMobile = useMediaQuery(MOBILE_QUERY)
  const collapsed = useUiStore((s) => s.sidebarCollapsed)
  const scrolled = useUiStore((s) => s.contentScrolled)
  const toggleSidebar = useUiStore((s) => s.toggleSidebar)
  const openDrawer = useUiStore((s) => s.setMobileSidebarOpen)
  const newChat = useNewChat()
  const showControls = isMobile || collapsed

  return (
    <header
      className={cn(
        'z-20 flex h-14 shrink-0 items-center gap-1 px-3 transition-[background-color,box-shadow] duration-300 sm:px-4',
        floating
          ? 'absolute inset-x-0 top-0 bg-transparent'
          : cn('glass-canvas relative', scrolled ? 'shadow-[0_0.5px_0_rgb(var(--ink)/0.1)]' : 'shadow-none'),
      )}
    >
      {isMobile && (
        <IconButton label="Open menu" onClick={() => openDrawer(true)}>
          <MenuIcon />
        </IconButton>
      )}
      {!isMobile && collapsed && (
        <IconButton label="Expand sidebar" onClick={toggleSidebar}>
          <PanelLeftOpen />
        </IconButton>
      )}
      {showControls && (
        <IconButton label="New chat" onClick={newChat}>
          <SquarePen />
        </IconButton>
      )}
      <div className="relative ml-1 h-5 min-w-0 flex-1">
        <AnimatePresence initial={false} mode="popLayout">
          {title ? (
            <motion.h1
              key={typeof title === 'string' ? title : 'title'}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: ease.out }}
              className="absolute inset-0 truncate text-[14px] font-semibold leading-5 tracking-snug text-ink"
            >
              {title}
            </motion.h1>
          ) : null}
        </AnimatePresence>
      </div>
      {children && <div className="flex shrink-0 items-center gap-1">{children}</div>}
    </header>
  )
}
